import React from "react";
import { Teller, SectieBalk, InternBlok, Leeg, ReactiesVoorJou } from "./VandaagParts";
import { AfdelingenBlok } from "./AfdelingenBlok";
import { RedenChips, PerOmKaart } from "@/components/overzicht/NuParts";
import { terugkerendChip } from "@/components/overzicht/Strook";
import { VoorJou, GroenPerWeek, VakerRood } from "./CeoParts";
import { CallsDezeWeek } from "./CallsDezeWeek";

const WowTeller = ({ w }) => (
  <Teller testid="teller-wow" label="WOW ingevuld" value={w?.laatst ? `${w.laatst.x} van ${w.laatst.y} calls` : "–"}
    sub={[w?.laatst ? `over week ${w.laatst.overWeekNr}` : null, w?.lopend ? `vandaag: ${w.lopend.x} van ${w.lopend.y}` : null].filter(Boolean).join(" · ")} />
);

const Groen = ({ g, extra }) => (
  <Teller testid="teller-groen" label={g ? `Groen over week ${g.overWeekNr}` : "Groen"} value={g ? `${g.opKoers}/${g.locaties}` : "–"}
    sub={[g?.vorige ? `week ${g.vorige.overWeekNr}: ${g.vorige.opKoers}` : null, extra].filter(Boolean).join(" · ")} />
);

// R2-F8: rose alleen als de locatie rood of terugkerend is (zoals R18).
const OpenAantal = ({ n, testid, rood }) => <span data-testid={testid} className={`font-mono text-xs ${n === 0 && rood ? "text-rose-600 font-bold" : "text-zinc-500"}`}>{n} open {n === 1 ? "afspraak" : "afspraken"}</span>;


export function CooVandaag({ d, onOpen, onGezien }) {
  return (
    <>
      <ReactiesVoorJou items={d.reactiesVoorJou} onGezien={onGezien} />
      <AfdelingenBlok items={d.afdelingen} />
      <CallsDezeWeek />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-3" data-testid="vandaag-tellers">
        <Groen g={d.groen} />
        <WowTeller w={d.wow} />
        <Teller testid="teller-aandacht" label="Aandacht nu" value={d.aandachtAantal} />
        <Teller testid="teller-te-laat" label="Afspraken te laat" sub="alle locaties en Hoofdkantoor, Truckshop 1, Truckssupply" value={d.teLaat} rood={d.teLaat > 0} />
        <Teller testid="teller-wacht-op-om" label="Wacht op OM" sub="de locatie of afdeling meldde" value={d.wachtOpOm ?? 0} />
        <Teller testid="teller-gebeld" label={`Gebeld ${d.callWeek.callLabel} · over week ${d.callWeek.overWeekNr}`} value={`${d.gebeld.x}/${d.gebeld.y}`} />
      </div>
      {d.trend && <p data-testid="vandaag-trend" className="text-sm text-zinc-700 mb-3 md:mb-4">{d.trend}</p>}
      <div className="mb-3 md:mb-4">
        <SectieBalk testid="vandaag-sectie-aandacht" titel="Aandacht nodig" aantal={d.aandacht.length} />
        {d.aandacht.length === 0 ? <Leeg>Niets dat aandacht vraagt.</Leeg> : (
          <div className="bg-white border border-zinc-200 rounded-[10px] divide-y divide-zinc-200">
            {d.aandacht.map((r) => (
              <button key={r.locatie} type="button" data-testid={`vandaag-aandacht-${r.locatie}`} onClick={() => onOpen(r.locatie)} className="w-full text-left flex flex-wrap items-center gap-2 px-3 py-2.5 hover:bg-zinc-50 transition-colors">
                <span className="font-heading font-black text-zinc-900 min-w-[120px]">{r.locatie}</span>
                <RedenChips r={r} />
                {r.terugkerend && terugkerendChip(r)}
                <OpenAantal n={r.open} rood={r.terugkerend || (r.redenen || []).some((x) => x.code.startsWith("rood"))} testid={`vandaag-aandacht-open-${r.locatie}`} />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="mb-3 md:mb-4">
        <SectieBalk testid="vandaag-sectie-per-om" titel={`Per OM · week ${d.perOmWeekNr} · call ${d.callWeek.callLabel}`} hint="tellingen, geen oordeel" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{d.perOm.map((o) => <PerOmKaart key={o.om} o={o} />)}</div>
      </div>
      <InternBlok items={d.internMetJou} />
    </>
  );
}

// v14: rustige CEO-pagina — de week in één zin, drie witte tegels, één lijst "Voor jou", groen per week.
export function CeoVandaag({ d, onOpen, onGezien }) {
  const g = d.groen;
  const gw = g ? d.weekstand.find((b) => b.week === g.week) : null;
  return (
    <>
      <p data-testid="vandaag-ceo-sub" className="text-sm text-zinc-500 mb-4">Eens per week kijken is genoeg; de details staan in het Overzicht.</p>
      <CallsDezeWeek />
      <AfdelingenBlok items={d.afdelingen} />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3 mb-3 md:mb-4" data-testid="vandaag-tellers">
        <Groen g={g} />
        <Teller testid="teller-gebeld" label="Gebeld" value={gw ? `${gw.gebeld}/${gw.locaties}` : "–"} sub={g ? `over week ${g.overWeekNr}` : null} />
        <div className="col-span-2 sm:col-span-1"><WowTeller w={d.wow} /></div>
      </div>
      <VoorJou items={d.voorJou || []} onOpen={onOpen} onGezien={onGezien} />
      <div className="mb-3 md:mb-4">
        <SectieBalk testid="vandaag-sectie-groen-per-week" titel="Groen per week" hint="13 weken" />
        <GroenPerWeek d={d} />
      </div>
      {d.vakerRood && <VakerRood v={d.vakerRood} />}
      {d.internMetJou.length > 0 ? <InternBlok items={d.internMetJou} />
        : <p data-testid="vandaag-intern-leeg" className="text-xs text-zinc-500 mb-3 md:mb-4">Geen open interne afspraken met jou.</p>}
    </>
  );
}
